import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../contexts/CompanyContext';
import { companiesApi } from '../../api/companies';
import { portalAdminApi } from '../../api/portal';
import { Copy, Plus, Trash2, ExternalLink, Building2, ToggleLeft, ToggleRight, KeyRound, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import type { PortalJoinCode } from '../../types';
import EmptyState from '../EmptyState';

export default function PortalSettingsTab() {
  const { currentCompany, refreshCompanies } = useCompany();
  const queryClient = useQueryClient();
  const settings = (currentCompany as any)?.settings ?? {};

  // ── Portal Settings ──
  const [portalEnabled, setPortalEnabled] = useState(!!settings.allowCustomerPortal);
  const [portalSearchable, setPortalSearchable] = useState(!!settings.portalSearchable);
  const [welcomeMessage, setWelcomeMessage] = useState(settings.portalWelcomeMessage ?? '');

  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const saveSettingsMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => companiesApi.update(companyId, { settings: { ...settings, ...data } } as any),
    onSuccess: () => {
      toast.success('Portal settings saved');
      refreshCompanies();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate({
      allowCustomerPortal: portalEnabled,
      portalSearchable,
      portalWelcomeMessage: welcomeMessage || undefined,
    });
  };

  // ── Join Codes ──
  const { data: joinCodes = [], isLoading: codesLoading } = useQuery({
    queryKey: ['portal-join-codes', (currentCompany as any)?.id],
    queryFn: () => portalAdminApi.listJoinCodes(),
    enabled: !!currentCompany,
  });

  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCodeLabel, setNewCodeLabel] = useState('');
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('');

  const createCodeMutation = useMutation({
    mutationFn: () => portalAdminApi.createJoinCode({
      label: newCodeLabel || undefined,
      maxUses: newCodeMaxUses ? parseInt(newCodeMaxUses) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-join-codes'] });
      setShowCreateCode(false);
      setNewCodeLabel('');
      setNewCodeMaxUses('');
      toast.success('Join code created');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleCodeMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      portalAdminApi.updateJoinCode(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-join-codes'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteCodeMutation = useMutation({
    mutationFn: (id: string) => portalAdminApi.deleteJoinCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-join-codes'] });
      toast.success('Join code deactivated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Customers ──
  const { data: customers = [] } = useQuery({
    queryKey: ['portal-customers', (currentCompany as any)?.id],
    queryFn: () => portalAdminApi.listCustomers(),
    enabled: !!currentCompany,
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const portalUrl = (code: string) => `${window.location.origin}/portal/join/${code}`;

  return (
    <div className="space-y-8 max-w-3xl">
      {/* ── Portal Toggle ── */}
      <div className="card p-6 space-y-5">
        <h2 className="section-title">Portal Settings</h2>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-fg">Enable Customer Portal</p>
            <p className="text-xs text-muted">Allow customers to register and submit tickets</p>
          </div>
          <button onClick={() => setPortalEnabled(!portalEnabled)} className="text-primary">
            {portalEnabled
              ? <ToggleRight className="w-8 h-8" />
              : <ToggleLeft className="w-8 h-8 text-muted" />
            }
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-fg">Searchable</p>
            <p className="text-xs text-muted">Customers can find your company by name when registering</p>
          </div>
          <button onClick={() => setPortalSearchable(!portalSearchable)} className="text-primary">
            {portalSearchable
              ? <ToggleRight className="w-8 h-8" />
              : <ToggleLeft className="w-8 h-8 text-muted" />
            }
          </button>
        </div>

        <div>
          <label className="label">Welcome Message (optional)</label>
          <textarea
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            placeholder="Welcome! Submit a ticket and we'll get back to you promptly."
            rows={3}
            className="input resize-y"
          />
        </div>

        <button onClick={handleSaveSettings} disabled={saveSettingsMutation.isPending} className="btn btn-primary">
          {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* ── Join Codes ── */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="section-title !mb-0">Join Codes</h2>
          <button onClick={() => setShowCreateCode(true)} className="btn btn-primary text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            New Code
          </button>
        </div>
        <p className="text-xs text-muted">
          Share a join link with customers — opening it takes them straight to your portal.
          They can also enter the short code manually on the "Join a Company" page.
        </p>

        {showCreateCode && (
          <div className="bg-row-odd p-4 rounded-lg space-y-3 border border-line">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Label (optional)</label>
                <input
                  type="text"
                  value={newCodeLabel}
                  onChange={(e) => setNewCodeLabel(e.target.value)}
                  placeholder="e.g., Website Link"
                  className="input"
                />
              </div>
              <div>
                <label className="label">Max Uses (optional)</label>
                <input
                  type="number"
                  value={newCodeMaxUses}
                  onChange={(e) => setNewCodeMaxUses(e.target.value)}
                  placeholder="Unlimited"
                  className="input"
                  min={1}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => createCodeMutation.mutate()} disabled={createCodeMutation.isPending} className="btn btn-primary text-sm">
                {createCodeMutation.isPending ? 'Creating...' : 'Create'}
              </button>
              <button onClick={() => setShowCreateCode(false)} className="btn btn-ghost text-sm">Cancel</button>
            </div>
          </div>
        )}

        {codesLoading ? (
          <p className="text-sm text-muted">Loading codes...</p>
        ) : joinCodes.length === 0 ? (
          <EmptyState icon={<KeyRound className="w-6 h-6" />}>
            No join codes yet. Click <strong>New Code</strong> to generate a link customers can use to join.
          </EmptyState>
        ) : (
          <div className="space-y-2">
            {(joinCodes as PortalJoinCode[]).map((jc) => (
              <div
                key={jc.id}
                className={`flex items-center gap-4 px-4 py-3 rounded-lg border ${
                  jc.isActive
                    ? 'border-line bg-surface'
                    : 'border-line bg-row-odd opacity-60'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono font-bold text-fg">
                      {jc.code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(jc.code)}
                      title="Copy code only"
                      className="p-1 rounded hover:bg-row-odd text-muted hover:text-fg"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                    {jc.label && (
                      <span className="badge text-xs text-muted">
                        {jc.label}
                      </span>
                    )}
                    {!jc.isActive && (
                      <span className="badge text-xs text-err">
                        Inactive
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => copyToClipboard(portalUrl(jc.code))}
                    title="Copy join link"
                    className="flex items-center gap-1.5 mt-1 max-w-full text-xs font-mono text-primary hover:underline"
                  >
                    <span className="truncate">{portalUrl(jc.code)}</span>
                    <Copy className="w-3 h-3 shrink-0" />
                  </button>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted">
                    <span>{jc.currentUses}{jc.maxUses ? ` / ${jc.maxUses}` : ''} uses</span>
                    {jc.crmOrganization && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {jc.crmOrganization.name}
                      </span>
                    )}
                    {jc.expiresAt && <span>Expires: {new Date(jc.expiresAt).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => copyToClipboard(portalUrl(jc.code))}
                    className="btn btn-secondary text-xs py-1 px-2.5 inline-flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Copy link
                  </button>
                  <button
                    onClick={() => toggleCodeMutation.mutate({ id: jc.id, isActive: !jc.isActive })}
                    title={jc.isActive ? 'Deactivate' : 'Activate'}
                    className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-fg"
                  >
                    {jc.isActive
                      ? <ToggleRight className="w-4 h-4 text-primary" />
                      : <ToggleLeft className="w-4 h-4" />
                    }
                  </button>
                  <button
                    onClick={() => deleteCodeMutation.mutate(jc.id)}
                    title="Delete"
                    className="p-1.5 rounded hover:bg-row-odd text-muted hover:text-err"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Customers ── */}
      <div className="card p-6 space-y-4">
        <h2 className="section-title">Portal Customers</h2>
        {customers.length === 0 ? (
          <EmptyState icon={<Users className="w-6 h-6" />}>
            No customers have joined via the portal yet. Share a join code above to invite them.
          </EmptyState>
        ) : (
          <div className="space-y-2">
            {customers.map((c: any) => (
              <div key={c.id} className="flex items-center gap-4 px-4 py-3 rounded-lg bg-row-odd">
                <div className="w-8 h-8 rounded-full brand-gradient flex items-center justify-center text-white text-sm font-semibold">
                  {(c.user?.name ?? '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-fg">{c.user?.name}</p>
                  <p className="text-xs text-muted">{c.user?.email}</p>
                </div>
                {c.crmContact?.organization && (
                  <span className="flex items-center gap-1 text-xs text-muted">
                    <Building2 className="w-3 h-3" />
                    {c.crmContact.organization.name}
                  </span>
                )}
                <span className="text-xs text-muted">
                  Joined {new Date(c.joinedAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
